import { ref, computed, defineComponent, watch, nextTick, provide, reactive, onMounted, onUpdated, } from 'vue';
import 'vue';
// import { useEventListener } from '@vueuse/core';
import useConfigInject from '../../_util/hooks/useConfigInject';
import classNames from '../../_util/classNames';
import { addUnit } from '../../_util/util';
import { isNumber, isObject } from '../../_util/hooks/_vueuse/is';
import { useResizeObserver } from '../../_util/hooks/_vueuse/useResizeObserver';
import { useEventListener } from '../../_util/hooks/_vueuse/useEventListener';
import { GAP } from './utils';
import { scrollbarContextKey } from './constants';
import Bar from './bar';
export const scrollbarProps = () => ({
    /**
     * @description height of scrollbar
     */
    height: {
        type: [String, Number],
        default: '',
    },
    /**
     * @description max height of scrollbar
     */
    maxHeight: {
        type: [String, Number],
        default: '',
    },
    /**
     * @description whether to use the native scrollbar
     */
    native: {
        type: Boolean,
        default: false,
    },
    /**
     * @description style of wrap
     */
    wrapStyle: { type: Object, default: undefined },
    /**
     * @description class of wrap
     */
    wrapClass: {
        type: [String, Array],
        default: '',
    },
    /**
     * @description class of view
     */
    viewClass: {
        type: [String, Array],
        default: '',
    },
    /**
     * @description style of view
     */
    viewStyle: {
        type: [String, Array, Object],
        default: '',
    },
    /**
     * @description do not respond to container size changes, if the container size does not change, it is better to set it to optimize performance
     */
    noresize: Boolean,
    /**
     * @description element tag of the view
     */
    component: String,
    /**
     * @description always show
     */
    always: Boolean,
    /**
     * @description minimum size of scrollbar
     */
    minSize: {
        type: Number,
        default: 20,
    },
});
const Scrollbar = defineComponent({
    name: 'WdScrollbar',
    props: scrollbarProps(),
    emits: ['scroll'],
    setup(props, { emit, slots, expose }) {
        const COMPONENT_NAME = 'scrollbar';
        const { prefixCls } = useConfigInject('scrollbar', props);
        let stopResizeObserver = undefined;
        let stopResizeListener = undefined;
        const scrollbarRef = ref();
        const wrapRef = ref();
        const resizeRef = ref();
        const sizeWidth = ref('0');
        const sizeHeight = ref('0');
        const barRef = ref();
        const ratioY = ref(1);
        const ratioX = ref(1);
        const style = computed(() => {
            const style = {};
            if (props.height)
                style.height = addUnit(props.height);
            if (props.maxHeight)
                style.maxHeight = addUnit(props.maxHeight);
            return [props.wrapStyle, style];
        });
        const handleScroll = () => {
            if (wrapRef.value) {
                barRef.value?.handleScroll(wrapRef.value);
                emit('scroll', {
                    scrollTop: wrapRef.value.scrollTop,
                    scrollLeft: wrapRef.value.scrollLeft,
                });
            }
        };
        function scrollTo(arg1, arg2) {
            if (isObject(arg1)) {
                wrapRef.value.scrollTo(arg1);
            }
            else if (isNumber(arg1) && isNumber(arg2)) {
                wrapRef.value.scrollTo(arg1, arg2);
            }
        }
        const setScrollTop = (value) => {
            if (!isNumber(value)) {
                console.warn(COMPONENT_NAME, 'value must be a number');
                return;
            }
            wrapRef.value.scrollTop = value;
        };
        const setScrollLeft = (value) => {
            if (!isNumber(value)) {
                console.warn(COMPONENT_NAME, 'value must be a number');
                return;
            }
            wrapRef.value.scrollLeft = value;
        };
        const update = () => {
            if (!wrapRef.value)
                return;
            const offsetHeight = wrapRef.value.offsetHeight - GAP;
            const offsetWidth = wrapRef.value.offsetWidth - GAP;
            const originalHeight = offsetHeight ** 2 / wrapRef.value.scrollHeight;
            const originalWidth = offsetWidth ** 2 / wrapRef.value.scrollWidth;
            const height = Math.max(originalHeight, props.minSize);
            const width = Math.max(originalWidth, props.minSize);
            ratioY.value =
                originalHeight / (offsetHeight - originalHeight) / (height / (offsetHeight - height));
            ratioX.value =
                originalWidth / (offsetWidth - originalWidth) / (width / (offsetWidth - width));
            sizeHeight.value = height + GAP < offsetHeight ? `${height}px` : '';
            sizeWidth.value = width + GAP < offsetWidth ? `${width}px` : '';
        };
        watch(() => props.noresize, noresize => {
            if (noresize) {
                stopResizeObserver?.();
                stopResizeListener?.();
            }
            else {
                ({ stop: stopResizeObserver } = useResizeObserver(resizeRef, update));
                stopResizeListener = useEventListener('resize', update);
            }
        }, { immediate: true });
        watch(() => [props.maxHeight, props.height], () => {
            if (!props.native)
                nextTick(() => {
                    update();
                    if (wrapRef.value) {
                        barRef.value?.handleScroll(wrapRef.value);
                    }
                });
        });
        provide(scrollbarContextKey, reactive({
            scrollbarElement: scrollbarRef,
            wrapElement: wrapRef,
        }));
        onMounted(() => {
            if (!props.native)
                nextTick(() => {
                    update();
                });
        });
        onUpdated(() => update());
        expose({
            /** @description scrollbar wrap ref */
            wrapRef,
            /** @description update scrollbar state manually */
            update,
            /** @description scrolls to a particular set of coordinates */
            scrollTo,
            /** @description set distance to scroll top */
            setScrollTop,
            /** @description set distance to scroll left */
            setScrollLeft,
            /** @description handle scroll event */
            handleScroll,
        });
        return () => {
            const wrapKls = classNames(`${props.wrapClass}`, {
                [`${prefixCls.value}-wrap`]: true,
                [`${prefixCls.value}-wrap-hidden-default`]: !props.native,
            });
            const resizeKls = classNames(`${props.viewClass}`, {
                [`${prefixCls.value}-view`]: true,
            });
            const { component: Component = 'div', always, viewStyle, native } = props;
            let barNode = null;
            if (!native) {
                barNode = (<Bar ref={barRef} height={sizeHeight.value} width={sizeWidth.value} always={always} ratio-x={ratioX.value} ratio-y={ratioY.value}/>);
            }
            return (<div class={`${prefixCls.value}`} ref={scrollbarRef}>
          <div ref={wrapRef} class={wrapKls} style={style.value} onScroll={handleScroll}>
            <Component ref={resizeRef} class={resizeKls} style={viewStyle}>
              {slots.default?.()}
            </Component>
          </div>
          {barNode}
        </div>);
        };
    },
});
export default Scrollbar;
